import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { Search, History, FileBarChart2, RefreshCw, Info, Inbox, Download, CalendarDays, TrendingUp } from "lucide-react";
import AppContext from "../../context/AppContext";
import { toast } from "../../utils/toast";
import { fetchExportData, fetchReportList, getWorkflowInputValueList, getCycleListByWorkflowId, generateCyclePerformanceReport } from "../../service/Task"; // Add `fetchReportList` API call
import MultipleActivitySelect from "../../components/common/MultipleActivitySelect";
import { Spinner } from "../../Spinner";
import translations from "../../components/common/Translations";
import CommonButton from "../../components/common/CommonButton";
import { Button } from "../../components/common/Button";
import TaskHistory from "../TaskHistory/TaskHistory";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PageSizeSelector from "../../components/common/PageSizeSelector";
import ReactSelector from "../../components/common/CommonReactSelector";
import { isRTL } from "../../utils/rtl";
import PagerArrowButton from "../../components/common/PagerArrowButton";
import Tooltip from "../../components/common/HoverTooltip";
import TableScrollShell from "../../components/common/TableScrollShell";
import ReportFailedStatus from "../../components/common/ReportFailedStatus";
import ReportNoRecordsStatus from "../../components/common/ReportNoRecordsStatus";
import { getReportLink, getReportStatus, isPerformanceReport, isReportInProgress, REPORT_STATUS } from "../../utils/reportStatus";

// Cycles come back as stored ("march-2026") and are echoed back verbatim on every
// request, so only the label is prettified -- and only when it actually looks like
// a month-year cycle. Anything else is shown as-is.
const CYCLE_MONTHS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

const parseCycle = (cycle) => {
  const match = /^([a-zA-Z]+)-(\d{4})$/.exec(String(cycle));
  if (!match) return null;
  const monthIndex = CYCLE_MONTHS.indexOf(match[1].toLowerCase());
  if (monthIndex === -1) return null;
  return { monthIndex, year: Number(match[2]) };
};

const formatCycleLabel = (cycle) => {
  const raw = String(cycle);
  const parsed = parseCycle(raw);
  if (!parsed) return raw;
  const month = CYCLE_MONTHS[parsed.monthIndex];
  return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${parsed.year}`;
};

/* The summary line under the toolbar is a sentence, not a row of labels, so it
   cannot be assembled from translated fragments -- Arabic and Urdu order the
   verb and the prepositions differently from English. Each language instead
   owns whole templates with {placeholders}, and this splices the values in.
   Unknown placeholders are left visible rather than silently blanked, so a
   typo in a template shows up instead of quietly losing a date. */
const formatTemplate = (template, values = {}) =>
  String(template ?? "").replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
  );

// Newest cycle first. Anything that isn't a month-year cycle sorts to the bottom
// alphabetically rather than being dropped.
const compareCyclesDesc = (a, b) => {
  const parsedA = parseCycle(a);
  const parsedB = parseCycle(b);
  if (parsedA && parsedB) {
    return parsedB.year - parsedA.year || parsedB.monthIndex - parsedA.monthIndex;
  }
  if (parsedA) return -1;
  if (parsedB) return 1;
  return String(a).localeCompare(String(b));
};

// Title + tab switcher, mounted at the top of whichever tab's card is
// currently showing (Export Report's toolbar card, or Account History's
// first card via TaskHistory's `headerSlot` prop) rather than as its own
// separate floating block above both.
const AccountReportsHeader = ({ activeTab, setActiveTab, theme, langMode, isRtl, extraActions = null }) => (
  <div className="flex flex-wrap items-start justify-between gap-3">
    <div className="min-w-0 flex-1 basis-52">
      <h4
        className={`inline-flex max-w-full items-center gap-2 whitespace-normal break-words text-base font-bold leading-tight sm:text-lg ${theme === "dark"
          ? "text-gray-100"
          : theme === "high-contrast"
            ? "high-contrast"
            : "text-slate-800"
          }`}
        data-testid="report-center"
      >
        {translations[langMode].accountreportcenter}
      </h4>
      <p className={`mt-0.5 text-xs ${theme === "dark" ? "text-gray-400" : theme === "high-contrast" ? "high-contrast" : "text-slate-500"}`}>
        {translations[langMode]?.accountreportcentersub ?? "Track account history and manage report exports."}
      </p>
    </div>

    <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
      <div
        className={`flex w-full items-center gap-1 rounded-[10px] p-1 sm:w-auto ${
          theme === "dark" ? "bg-gray-800" : theme === "high-contrast" ? "high-contrast border-2" : "bg-gray-100/80"
        }`}
        role="group"
      >
        <Tooltip
          text={translations[langMode]?.accHistoryTabTooltip ?? "View the history of an account"}
          position="top"
          wrapperClassName="flex-1 sm:flex-initial"
        >
          <Button
            onClick={() => setActiveTab("account-history")}
            isActive={activeTab === 'account-history'}
            aria-label="account-history"
            className="h-10 min-h-[40px] w-full justify-center gap-1.5 rounded-[8px] px-2 py-2 text-xs sm:w-auto sm:px-4 sm:text-sm"
          >
            <History size={15} className="shrink-0" />
            {translations[langMode].acchistory}
          </Button>
        </Tooltip>

        <Tooltip
          text={translations[langMode]?.exportReportTabTooltip ?? "Request and download reports"}
          position="top"
          wrapperClassName="flex-1 sm:flex-initial"
        >
          <Button
            onClick={() => setActiveTab("export-report")}
            isActive={activeTab === 'export-report'}
            aria-label="Export Report Dashboard"
            className="h-10 min-h-[40px] w-full justify-center gap-1.5 rounded-[8px] px-2 py-2 text-xs sm:w-auto sm:px-4 sm:text-sm"
          >
            <FileBarChart2 size={15} className="shrink-0" />
            {translations[langMode].exportreport}
          </Button>
        </Tooltip>
      </div>

      {extraActions}
    </div>
  </div>
);

const ExportData = () => {
  const context = useContext(AppContext);
  const { theme, langMode, selectedActivityNames, setSelectedActivityNames, setHeaderActivitySelect } = context;
  const isRtl = isRTL(langMode);
  const formatShortDate = (date) =>
    date ? date.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;
  };
  const formattedDate = (dateString) => {
    if (!dateString) {
      return "-";
    }
    const date = new Date(dateString);
    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true, // Use 12-hour format with AM/PM
    };

    const timeZone = dateString.match(/([+-]\d{2}:\d{2})$/)?.[1] || 'UTC';
    options.timeZone = timeZone;
    return date.toLocaleString('en-US', options);
  };
  const today = new Date();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedOption, setSelectedOption] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isIncludeDeletedRecords, setIsIncludeDeletedRecords] = useState(true)
  const [totalValue, setTotalValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10); // Number of rows per page
  const [activeTab, setActiveTab] = useState("account-history");
  const [workflowNameSearch, setWorkflowNameSearch] = useState("");
  const [workflowInputValueOptions, setWorkflowInputValueOptions] = useState([]);
  const [selectedWorkflowInputValue, setSelectedWorkflowInputValue] = useState(null);
  const [cycleOptions, setCycleOptions] = useState([]);
  const [selectedCycles, setSelectedCycles] = useState([]);
  // Which cycles each selected workflow actually has, so performance-report pairs
  // can skip combinations that don't exist rather than sending empty requests.
  const [cyclesByWorkflow, setCyclesByWorkflow] = useState({});
  // generate-report accepts exactly one scope -- a date range or a cycle -- and
  // rejects both or neither. An explicit toggle keeps that unambiguous: the
  // inactive scope's control is disabled and its value is never sent.
  const [reportScope, setReportScope] = useState("date");
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [isPerformanceLoading, setIsPerformanceLoading] = useState(false);
  const dateTriggerRef = useRef(null);

  const isCycleScope = reportScope === "cycle";
  const isSingleActivitySelected =
    Array.isArray(selectedOption) && selectedOption.length === 1;
  const hasActivitySelected = Array.isArray(selectedOption) && selectedOption.length > 0;

  /* Cycle scope covers exactly one activity. Cycles are stored per workflow, so
     several activities produce a union of cycle names that no longer says which
     activity each belongs to -- and both reports are read per activity anyway.
     Rather than let that ambiguity through, the whole scope is closed off until
     the selection is a single activity. */
  const isCycleScopeAvailable = isSingleActivitySelected;
  const cycleScopeDisabledReason = isCycleScopeAvailable
    ? ""
    : hasActivitySelected
      ? translations[langMode].cyclesingleactivityonly
      : translations[langMode].cycleselectactivityfirst;

  // Every selected activity crossed with every selected cycle, minus the pairs
  // that activity has no such cycle for. Cycle-major so the payload groups by
  // cycle the way the backend's examples do.
  const performanceRequests = useMemo(() => {
    // Single activity only, same rule as the scope itself -- this is the gate
    // the generate button reads, so it has to agree with isCycleScopeAvailable.
    if (!isCycleScopeAvailable || !selectedCycles.length) return [];

    const pairs = [];
    selectedCycles.forEach((cycleOption) => {
      selectedOption.forEach((workflowId) => {
        const available = cyclesByWorkflow[String(workflowId)] || [];
        if (available.includes(cycleOption.value)) {
          pairs.push({ workflowId, cycle: cycleOption.value });
        }
      });
    });
    return pairs;
  }, [isCycleScopeAvailable, selectedOption, selectedCycles, cyclesByWorkflow]);

  const canRequestPerformanceReport = performanceRequests.length > 0;

  const handleScopeChange = (scope) => {
    setReportScope(scope);
    setIsDatePopoverOpen(false);
    // Only the active scope's value is ever sent, so drop the other one rather
    // than leaving a stale selection sitting in a disabled control.
    if (scope === "date") {
      setSelectedCycles([]);
    } else {
      setStartDate(today);
      setEndDate(today);
    }
  };

  useEffect(() => {
    if (!isDatePopoverOpen) return;
    const onDocMouseDown = (e) => {
      if (dateTriggerRef.current && !dateTriggerRef.current.contains(e.target)) {
        setIsDatePopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [isDatePopoverOpen]);

  // Render the activity picker into the top header bar (same slot the Task
  // List page uses) instead of inline in the toolbar, matching that page's
  // layout. Only while the Export Report tab is active -- Account History
  // doesn't use it -- and cleared on unmount so it doesn't leak into other
  // pages/tabs.
  useEffect(() => {
    if (!setHeaderActivitySelect) return undefined;

    if (activeTab !== "export-report") {
      setHeaderActivitySelect(null);
      return undefined;
    }

    setHeaderActivitySelect(
      <MultipleActivitySelect
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        isIncludeDeletedRecords={isIncludeDeletedRecords}
        setIsIncludeDeletedRecords={setIsIncludeDeletedRecords}
        hideIncludeDeletedCheckbox
        placeholder={translations[langMode].selectactivity || "Select Activity"}
      />
    );

    return () => setHeaderActivitySelect(null);
  }, [
    setHeaderActivitySelect,
    activeTab,
    selectedOption,
    isIncludeDeletedRecords,
    langMode,
  ]);

  const totalPages = Math.ceil(totalValue / pageSize) - 1;
  const filteredReportData = reportData.filter((r) =>
    (r.workflowNames || "")
      .toLowerCase()
      .includes(workflowNameSearch.trim().toLowerCase())
  );
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const totalItems = totalValue;
  const displayStart = totalItems > 0 ? startIndex + 1 : 0;
  const displayEnd = totalItems > 0 ? Math.min(endIndex, totalItems) : 0;
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const fetchReportListData = async () => {
    setIsLoading(true);
    try {
      const { reportRequestList, totalSize } = await fetchReportList(
        currentPage,
        pageSize
      );
      setReportData(reportRequestList || []);
      setTotalValue(totalSize || 0);
    } catch (error) {
      console.error("Error fetching report list:", error);
      toast.error(translations[langMode].failedFetchReports);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchReportListData();
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (activeTab !== "export-report") return;

    // Mirrors the Download Task Report modal's Recent Reports polling: this
    // page previously only fetched once on mount/page-change, so a report
    // that finished generating on the backend never showed its Download
    // button here until the user manually clicked Refresh. Poll quietly
    // (without touching isLoading, so the table doesn't flash a spinner)
    // as long as any row is still IN_PROGRESS -- every report ends up either
    // READY or FAILED, so polling always stops (see getReportStatus).
    const pollInterval = setInterval(() => {
      setReportData((current) => {
        if (current.some(isReportInProgress)) {
          fetchReportList(currentPage, pageSize).then(
            ({ reportRequestList, totalSize }) => {
              setReportData(reportRequestList || []);
              setTotalValue(totalSize || 0);
            }
          );
        }
        return current;
      });
    }, 8000);

    return () => clearInterval(pollInterval);
  }, [activeTab, currentPage, pageSize]);

  const fetchData = async () => {
    if (!selectedOption.length) {
      toast.error(translations[langMode].pleaseSelectLeastActivity);
      return;
    }

    // generate-report scopes to a single cycle, unlike the performance report
    // which takes many.
    if (isCycleScope && selectedCycles.length !== 1) {
      toast.error(
        selectedCycles.length === 0
          ? translations[langMode].pleaseselectacycle
          : translations[langMode].pleaseselectonecycle
      );
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchExportData(
        selectedOption,
        isCycleScope ? null : formatDate(startDate),
        isCycleScope ? null : formatDate(endDate),
        isIncludeDeletedRecords,
        selectedWorkflowInputValue?.value ?? null,
        isCycleScope ? selectedCycles[0]?.value : null
      );
      if (response.data.header.code === 600) {
        toast.success(response.data.body.value);
        fetchReportListData();
        setSelectedOption([]);
        setSelectedActivityNames([]);
      } else {
        toast.error(response.data.body.value);
      }
    } catch (error) {
      console.error("Error submitting report request:", error);
      toast.error(translations[langMode].failedSubmitReportRequest);
    } finally {
      setIsLoading(false);
    }
  };

  // Runs inline on the backend rather than being queued, so there is nothing to
  // poll for -- the finished row (carrying performanceCycleReport) is already in
  // the list by the time we refresh it.
  const handleCyclePerformanceReport = async () => {
    if (!canRequestPerformanceReport) return;

    setIsPerformanceLoading(true);
    try {
      const response = await generateCyclePerformanceReport(performanceRequests);
      if (response?.header?.code === 600) {
        toast.success(translations[langMode].performancereportready);
        fetchReportListData();
        setSelectedOption([]);
      } else {
        toast.error(response?.body?.value || translations[langMode].failedSubmitReportRequest);
      }
    } catch (error) {
      console.error("Error generating cycle performance report:", error);
      toast.error(translations[langMode].failedSubmitReportRequest);
    } finally {
      setIsPerformanceLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);

    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [activeTab]);

  useEffect(() => {
    // fetchReportListData(); // redundant: the effect above (deps:
    // [currentPage, pageSize]) already fetches this same data on mount.
    setSelectedActivityNames([]);
  }, []);

  useEffect(() => {
    const loadWorkflowInputValues = async () => {
      // Endpoint supports a single workflowId; if multi-selected, we disable/clear dropdown.
      if (!Array.isArray(selectedOption) || selectedOption.length !== 1) {
        setWorkflowInputValueOptions([]);
        setSelectedWorkflowInputValue(null);
        return;
      }

      const workflowId = selectedOption[0];
      const list = await getWorkflowInputValueList(workflowId);
      const getFirstValue = (source, keys) =>
        keys.map((key) => source?.[key]).find((value) => value != null && String(value).trim() !== "");

      const buildOptionLabel = (source, fallbackValue) => {
        const question = getFirstValue(source, [
          "question",
          "questionName",
          "fieldQuestion",
          "fieldQuestionName",
          "fieldLabel",
          "fieldName",
        ]);
        const option = getFirstValue(source, [
          "option",
          "optionName",
          "optionValue",
          "inputValue",
          "name",
          "label",
          "value",
        ]);

        if (question && option && String(question) !== String(option)) {
          return `${question}: ${option}`;
        }

        return String(question || option || fallbackValue || "");
      };

      const normalized = list
        .map((x) => {
          if (x == null) return null;

          if (typeof x === "string" || typeof x === "number") {
            return { value: String(x), label: String(x) };
          }

          const radioId =
            x.radioId != null && String(x.radioId).trim() !== ""
              ? String(x.radioId).trim()
              : "";

          if (radioId) {
            return {
              value: radioId,
              label: buildOptionLabel(x, radioId),
            };
          }

          const v =
            x.value ??
            x.inputValue ??
            x.name ??
            x.label ??
            x.workflowInputValue ??
            x.workflowInputValueName ??
            x.id ??
            "";

          if (v === "" || v == null) return null;

          return {
            value: String(v),
            label: buildOptionLabel(x, v),
          };
        })
        .filter(Boolean);
      setWorkflowInputValueOptions(normalized);
      setSelectedWorkflowInputValue(null);
    };

    loadWorkflowInputValues();
  }, [selectedOption]);

  // Cycles are stored per workflow, so each selected activity is fetched
  // separately: the dropdown offers the union, while the per-workflow lists are
  // kept so performance-report pairs can drop combinations that don't exist.
  useEffect(() => {
    let isStale = false;

    const loadCycles = async () => {
      /* Anything other than exactly one activity closes cycle scope, so bail
         out and fall back to the date range. Without the reset, adding a second
         activity while already in cycle scope would strand the user there with
         the toggle disabled and no way back. */
      if (!Array.isArray(selectedOption) || selectedOption.length !== 1) {
        setCyclesByWorkflow({});
        setCycleOptions([]);
        setSelectedCycles([]);
        setReportScope("date");
        return;
      }

      const lists = await Promise.all(
        selectedOption.map((workflowId) => getCycleListByWorkflowId(workflowId))
      );
      if (isStale) return;

      const byWorkflow = {};
      selectedOption.forEach((workflowId, index) => {
        byWorkflow[String(workflowId)] = (lists[index] || [])
          .filter((cycle) => cycle != null && String(cycle).trim() !== "")
          .map(String);
      });

      const union = Array.from(new Set(Object.values(byWorkflow).flat())).sort(
        compareCyclesDesc
      );

      setCyclesByWorkflow(byWorkflow);
      setCycleOptions(union.map((cycle) => ({ value: cycle, label: formatCycleLabel(cycle) })));
      setSelectedCycles([]);
    };

    loadCycles();
    return () => {
      isStale = true;
    };
  }, [selectedOption]);

  const accountReportsHeader = (
    <AccountReportsHeader
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      theme={theme}
      langMode={langMode}
      isRtl={isRtl}
    />
  );

  return (
    <div className="flex min-h-full sm:h-full sm:min-h-0 w-full min-w-0 flex-col overflow-y-auto sm:overflow-hidden">
      {isLoading && activeTab === "account-history" && <Spinner />}
      {activeTab === "account-history" ? (
        <div className="flex min-h-full sm:h-full sm:min-h-0 w-full min-w-0 flex-1 flex-col">
          <TaskHistory headerSlot={accountReportsHeader} />
        </div>
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 mx-2 mt-2 mb-2 sm:mx-4 sm:mt-3 sm:mb-3 sm:gap-3">
          <div
            className={`flex shrink-0 flex-col gap-3 rounded-[14px] border px-3 py-3 sm:gap-4 sm:px-5 sm:py-4 ${
              theme === "dark"
                ? "border-gray-700 bg-gray-800/60"
                : theme === "high-contrast"
                  ? "high-contrast border-2"
                  : "border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05),0_1px_3px_rgba(16,24,40,0.05)]"
            }`}
          >
            <AccountReportsHeader
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              theme={theme}
              langMode={langMode}
              isRtl={isRtl}
              extraActions={
                <div className={`flex w-full flex-wrap items-center justify-between gap-2 sm:w-auto sm:justify-start ${isRtl ? "flex-row-reverse" : ""}`}>
                  <Tooltip text={translations[langMode].includedeletedrecordstooltip} position="top" wrapperClassName="flex-1 sm:flex-initial">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isIncludeDeletedRecords}
                      aria-label={translations[langMode].IncludeDeletedRecords}
                      onClick={() => setIsIncludeDeletedRecords(!isIncludeDeletedRecords)}
                      className={`flex h-10 w-full shrink-0 items-center justify-center gap-2 rounded-[9px] border px-2.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#236DB4] focus:ring-offset-2 sm:w-auto ${theme === "dark" ? "focus:ring-offset-gray-800" : ""} ${
                        theme === "dark"
                          ? "border-gray-600 bg-gray-800 text-gray-300"
                          : theme === "high-contrast"
                            ? "high-contrast border-2"
                            : "border-gray-300 bg-white text-gray-700"
                      }`}
                    >
                      <span
                        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border-2 border-transparent overflow-hidden transition-colors ${isIncludeDeletedRecords ? "bg-[#236DB4]" : "bg-gray-300 dark:bg-gray-600"}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                            isIncludeDeletedRecords
                              ? isRtl
                                ? "-translate-x-4"
                                : "translate-x-4"
                              : "translate-x-0.5"
                          }`}
                        />
                      </span>
                      <span className="whitespace-nowrap">{translations[langMode].IncludeDeletedRecords}</span>
                    </button>
                  </Tooltip>

                  <CommonButton colorIntent="primary"
                    onClick={fetchReportListData}
                    variant="success"
                    tooltipText={translations[langMode].refeshreportlist}
                    tooltipPosition="top"
                    className="!h-10 !min-h-[40px] !rounded-[10px] !px-4 !py-2 text-xs shrink-0"
                  >
                    <RefreshCw size={16} />
                    {translations[langMode]?.refreshBtn ?? "Refresh"}
                  </CommonButton>
                </div>
              }
            />
            <div className={`flex flex-wrap items-center gap-3 ${isRtl ? "flex-row-reverse" : ""}`}>
              <div className={`flex w-full flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center ${isRtl ? "flex-row-reverse" : ""}`}>
                {/* Same activity multi-select the nav-bar header slot already shows
                    at >=1214px (this project's custom `lg`) -- that instance lives
                    inside a `hidden lg:flex` container in MainNav.js, so it's
                    unreachable inline below that width. Render a second, page-scoped
                    copy here for mobile/tablet instead of touching the shared nav
                    (which also feeds Dashboard.js/TaskListToolbar.js). Both stay in
                    sync since they're driven by the same state below. */}
                <div className="w-full sm:w-[220px] lg:hidden">
                  <MultipleActivitySelect
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    isIncludeDeletedRecords={isIncludeDeletedRecords}
                    setIsIncludeDeletedRecords={setIsIncludeDeletedRecords}
                    hideIncludeDeletedCheckbox
                    placeholder={translations[langMode].selectactivity || "Select Activity"}
                  />
                </div>

                <div className="w-full sm:min-w-[190px] sm:max-w-[320px] sm:flex-1">
                  <ReactSelector
                    value={selectedWorkflowInputValue}
                    options={workflowInputValueOptions}
                    onChange={(opt) => setSelectedWorkflowInputValue(opt)}
                    height="40px"
                    menuPlacement="bottom"
                    borderRadius="8px"
                    maxMenuHeight={230}
                    isDisabled={!isSingleActivitySelected}
                    isClearable
                    aria-label={translations[langMode]?.filteredReports ?? "Filter Reports"}
                    placeholder={
                      isSingleActivitySelected
                        ? (translations[langMode]?.select ?? "Select")
                        : (translations[langMode]?.selectRadioOption ?? "Select radio option")
                    }
                  />
                </div>

                <div className="flex w-full min-w-0 items-center gap-2 sm:contents">
                {/* Scope switch: the report is filtered by a date range or by a
                    cycle, never both -- the backend rejects a request carrying
                    either two scopes or none. */}
                <div
                  className={`inline-flex shrink-0 items-center gap-1 rounded-[10px] p-0.5 ${
                    theme === "dark"
                      ? "bg-gray-800"
                      : theme === "high-contrast"
                        ? "high-contrast border-2"
                        : "bg-gray-100/80"
                  }`}
                  role="group"
                  aria-label={translations[langMode].reportscope}
                >
                  <Button
                    onClick={() => handleScopeChange("date")}
                    isActive={!isCycleScope}
                    className="h-9 min-h-[36px] rounded-[8px] px-3 py-1 text-xs"
                    data-testid="export-report-scope-date"
                  >
                    {translations[langMode].daterange}
                  </Button>
                  {/* Tooltip renders its children untouched when the text is
                      empty, so this carries the "why is this greyed out"
                      message only while the tab is actually disabled. The
                      wrapper div is what catches the hover -- a disabled
                      button has pointer-events: none and would never fire. */}
                  <Tooltip text={cycleScopeDisabledReason} position="top">
                    <Button
                      onClick={() => handleScopeChange("cycle")}
                      isActive={isCycleScope}
                      disabled={!isCycleScopeAvailable}
                      className="h-9 min-h-[36px] rounded-[8px] px-3 py-1 text-xs"
                      data-testid="export-report-scope-cycle"
                    >
                      {translations[langMode].cycle}
                    </Button>
                  </Tooltip>
                </div>

                {/* Only the active scope's control is mounted -- the other one is
                    never sent, so leaving it on screen greyed out is just noise.
                    Multi-select because the performance report takes many cycles;
                    the data report still needs exactly one, which fetchData
                    enforces. */}
                {isCycleScope && (
                  <div className="min-w-0 flex-1 sm:flex-none sm:w-[220px]">
                    <ReactSelector
                      value={selectedCycles}
                      options={cycleOptions}
                      onChange={(opts) => setSelectedCycles(opts || [])}
                      isMulti
                      height="40px"
                      menuPlacement="bottom"
                      borderRadius="8px"
                      maxMenuHeight={230}
                      selectedValuesMaxHeight="76px"
                      isDisabled={!isCycleScopeAvailable}
                      isClearable
                      aria-label={translations[langMode]?.selectcycle ?? "Select Cycle"}
                      placeholder={
                        isCycleScopeAvailable && cycleOptions.length === 0
                          ? translations[langMode].nocyclesavailable
                          : (translations[langMode]?.selectcycle ?? "Select Cycle")
                      }
                      dataTestId="export-report-cycle-select"
                    />
                  </div>
                )}

                {!isCycleScope && (
                <div className="relative min-w-0 flex-1 sm:flex-none sm:w-auto" ref={dateTriggerRef}>
                  <Tooltip
                    text={translations[langMode].selectdurationtooltip}
                    position="top"
                    wrapperClassName="w-full sm:w-auto"
                  >
                    <button
                      type="button"
                      onClick={() => setIsDatePopoverOpen((o) => !o)}
                      aria-expanded={isDatePopoverOpen}
                      aria-label={`${translations[langMode].fromdate} / ${translations[langMode].todate}`}
                      data-testid="export-report-date-range-button"
                      /* The cycle control mirrors itself in RTL because
                         CommonReactSelector sets its own direction; this is a
                         raw button, so it needs the same flex-row-reverse every
                         other row on this screen uses or the calendar icon
                         stays stranded on the left in Arabic/Urdu. */
                      className={`flex h-10 w-full items-center justify-between gap-2 rounded-[10px] border px-3 text-sm font-medium transition-colors sm:w-auto sm:justify-start ${
                        isRtl ? "flex-row-reverse" : ""
                      } ${
                        theme === "dark"
                          ? "border-gray-600 bg-gray-800 text-gray-200 hover:border-gray-400"
                          : theme === "high-contrast"
                            ? "high-contrast border-2"
                            : "border-gray-300 bg-white text-gray-800 hover:border-[#236DB4]"
                      }`}
                    >
                      <CalendarDays size={15} className="shrink-0 opacity-60" />
                      <span className="whitespace-nowrap">
                        {startDate && endDate
                          ? `${formatShortDate(startDate)} – ${formatShortDate(endDate)}`
                          : translations[langMode].selectduration}
                      </span>
                    </button>
                  </Tooltip>

                  {isDatePopoverOpen && (
                    <div
                      className={`absolute top-full z-30 mt-1.5 w-[210px] max-w-[calc(100vw-2rem)] rounded-xl border p-3 shadow-lg ${isRtl ? "right-0" : "left-0"} ${
                        theme === "dark"
                          ? "border-gray-700 bg-gray-800"
                          : theme === "high-contrast"
                            ? "high-contrast border-2"
                            : "border-gray-200 bg-white"
                      }`}
                    >
                      <div className="flex flex-col gap-1">
                        <label className={`text-xs font-medium ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                          {translations[langMode].fromdate}
                        </label>
                        <DatePicker
                          selected={startDate}
                          onChange={(date) => {
                            setStartDate(date);
                            if (endDate && date && endDate < date) {
                              setEndDate(date);
                            }
                          }}
                          placeholderText={translations[langMode].fromdate}
                          maxDate={today}
                          selectsStart
                          startDate={startDate}
                          endDate={endDate}
                          wrapperClassName="w-full"
                          className={`h-9 w-full !rounded-lg border px-2 text-sm ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : "border-gray-300"}`}
                          calendarClassName="!rounded-lg shadow-lg"
                          popperPlacement="bottom-start"
                          popperContainer={({ children }) => (
                            <div style={{ position: "fixed", zIndex: 100000 }}>
                              {children}
                            </div>
                          )}
                        />
                      </div>

                      <div className="mt-3 flex flex-col gap-1">
                        <label className={`text-xs font-medium ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                          {translations[langMode].todate}
                        </label>
                        <DatePicker
                          selected={endDate}
                          onChange={(date) => {
                            setEndDate(date);
                          }}
                          placeholderText={translations[langMode].todate}
                          minDate={startDate}
                          maxDate={today}
                          selectsEnd
                          startDate={startDate}
                          endDate={endDate}
                          disabled={!startDate}
                          wrapperClassName="w-full"
                          className={`h-9 w-full !rounded-lg border px-2 text-sm ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : "border-gray-300"}`}
                          calendarClassName="!rounded-lg shadow-lg"
                          popperPlacement="bottom-start"
                          popperContainer={({ children }) => (
                            <div style={{ position: "fixed", zIndex: 100000 }}>
                              {children}
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>
                )}
                </div>

                <Tooltip text={translations[langMode].requestreporttooltip} position="top" wrapperClassName="w-full sm:w-auto">
                  <Button
                    onClick={fetchData}
                    disabled={isLoading}
                    isActive
                    className="h-10 min-h-[40px] w-full shrink-0 justify-center gap-1.5 rounded-[10px] px-3 py-2 text-xs sm:w-auto sm:px-4 sm:text-sm"
                  >
                    <FileBarChart2 size={15} className="shrink-0" />
                    {isLoading ? translations[langMode].Processing : translations[langMode].reportbtn}
                  </Button>
                </Tooltip>

                {/* Cycle performance report -- needs at least one activity/cycle
                    pair, so like the cycle dropdown it only appears in cycle
                    scope rather than sitting permanently disabled. */}
                {isCycleScope && (
                <Tooltip
                  text={
                    canRequestPerformanceReport
                      ? translations[langMode].performancereporttooltip
                      : // The activity rule outranks the generic "pick a cycle"
                        // hint: picking a cycle cannot help while the selection
                        // is still more than one activity.
                        cycleScopeDisabledReason ||
                        translations[langMode].performancereportrequirements
                  }
                  position="top"
                  wrapperClassName="w-full sm:w-auto"
                >
                  <Button
                    onClick={handleCyclePerformanceReport}
                    disabled={!canRequestPerformanceReport || isPerformanceLoading}
                    className="h-10 min-h-[40px] w-full shrink-0 gap-1.5 rounded-[10px] px-3 py-2 text-xs sm:w-auto sm:px-4 sm:text-sm"
                    data-testid="export-performance-report-button"
                  >
                    <TrendingUp size={15} className="shrink-0" />
                    {isPerformanceLoading
                      ? translations[langMode].Processing
                      : translations[langMode].cycleperformancereport}
                    {/* More than one activity/cycle pair is easy to build up by
                        accident, so the count is shown before committing. */}
                    {!isPerformanceLoading && performanceRequests.length > 1 && (
                      <span
                        className={`inline-flex items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                          theme === "dark" ? "bg-[#236DB4]/25 text-[#7fb3e0]" : "bg-[#EAF2FA] text-[#236DB4]"
                        }`}
                      >
                        {performanceRequests.length}
                      </span>
                    )}
                  </Button>
                </Tooltip>
                )}
              </div>

              <div className={`flex flex-wrap items-center gap-1.5 text-xs sm:text-[13px] ${isRtl ? "flex-row-reverse" : ""}`}>
              <span className={theme === "dark" ? "text-gray-500" : "text-gray-400"}>
                {translations[langMode].reportsummaryprefix}
              </span>
              {(selectedActivityNames || []).length === 0 ? (
                <>
                  <span className={`border-b border-dashed pb-px font-medium ${theme === "dark" ? "border-gray-600 text-gray-500" : "border-gray-300 text-gray-400"}`}>
                    {translations[langMode].selectactivities}
                  </span>
                  {/* Only this branch has a connector between the two blanks --
                      the branch below runs the activity names straight into the
                      range phrase, which is why the sentence is split into
                      prefix/connector/suffix rather than one template. */}
                  <span className={theme === "dark" ? "text-gray-500" : "text-gray-400"}>
                    {translations[langMode].reportsummarycovering}
                  </span>
                  <span className={`border-b border-dashed pb-px font-medium ${theme === "dark" ? "border-gray-600 text-gray-500" : "border-gray-300 text-gray-400"}`}>
                    {isCycleScope
                      ? translations[langMode].selectcycle
                      : translations[langMode].selectduration}
                  </span>
                </>
              ) : (
                <>
                  {selectedActivityNames.length <= 2 ? (
                    <span className="font-semibold text-[#236DB4]">
                      {selectedActivityNames.length === 1
                        ? selectedActivityNames[0]
                        : formatTemplate(translations[langMode].activitiesjoinand, {
                            first: selectedActivityNames[0],
                            second: selectedActivityNames[1],
                          })}
                    </span>
                  ) : (
                    <>
                      {/* Below sm: the sibling's overflow list only opens on
                          hover, which touch screens can't trigger -- show
                          every selected name directly instead (the row
                          already wraps/stacks on mobile, so extra height is
                          fine). At sm:+ this is hidden and the hover version
                          below takes over unchanged. */}
                      <span className="font-semibold text-[#236DB4] sm:hidden">
                        {selectedActivityNames.join(", ")}
                      </span>
                    <span className="group/activities relative hidden cursor-default items-center sm:inline-flex">
                      <span className="font-semibold text-[#236DB4]">
                        {selectedActivityNames[0]}, {selectedActivityNames[1]}{" "}
                        <span
                          className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-bold ${
                            theme === "dark" ? "bg-[#236DB4]/25 text-[#7fb3e0]" : "bg-[#EAF2FA] text-[#236DB4]"
                          }`}
                        >
                          +{selectedActivityNames.length - 2}
                        </span>
                      </span>

                      <div
                        className={`invisible absolute top-full z-30 mt-2 w-max min-w-[170px] max-w-[260px] -translate-y-1 rounded-xl border opacity-0 shadow-lg transition-all duration-150 group-hover/activities:visible group-hover/activities:translate-y-0 group-hover/activities:opacity-100 ${
                          isRtl ? "right-0" : "left-0"
                        } ${
                          theme === "dark"
                            ? "border-gray-700 bg-gray-800"
                            : theme === "high-contrast"
                              ? "high-contrast border-2"
                              : "border-gray-200 bg-white"
                        }`}
                      >
                        <div className={`border-b px-3 py-2 text-center text-[11px] font-bold uppercase tracking-wide ${theme === "dark" ? "border-gray-700 text-gray-500" : "border-gray-200 text-gray-400"}`}>
                          {formatTemplate(translations[langMode].activitieslistheader, {
                            count: selectedActivityNames.length,
                          })}
                        </div>
                        <ul className="max-h-[220px] overflow-y-auto py-1">
                          {selectedActivityNames.map((name, idx) => (
                            <li
                              key={`${name}-${idx}`}
                              className={`px-3 py-2 text-center text-sm font-medium ${theme === "dark" ? "text-gray-200" : "text-gray-700"}`}
                            >
                              {name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </span>
                    </>
                  )}
                  <span className={theme === "dark" ? "text-gray-500" : "text-gray-400"}>
                    {isCycleScope
                      ? selectedCycles.length
                        ? formatTemplate(translations[langMode].reportrangecycles, {
                            cycles: selectedCycles.map((option) => option.label).join(", "),
                          })
                        : translations[langMode].selectcycle
                      : formatTemplate(translations[langMode].reportrangedates, {
                          from: formatShortDate(startDate),
                          to: formatShortDate(endDate),
                        })}
                  </span>
                </>
              )}
              {/* Verb-final languages (Urdu) close the sentence after the range;
                  English and Arabic leave this empty and render nothing. */}
              {translations[langMode].reportsummarysuffix && (
                <span className={theme === "dark" ? "text-gray-500" : "text-gray-400"}>
                  {translations[langMode].reportsummarysuffix}
                </span>
              )}
              </div>
            </div>

            {/* Note Banner */}
            <div className={`w-full sm:w-auto inline-flex max-w-full self-start items-start gap-1.5 rounded-[8px] border px-2.5 py-1.5 text-[11px] leading-snug font-medium sm:text-xs lg:text-[12px] 2xl:text-[14px] ${
              theme === "dark"
                ? "bg-amber-950/15 border-amber-900/30 text-amber-300"
                : theme === "high-contrast"
                  ? "bg-black border-white text-white"
                  : "bg-amber-50 border-amber-200 text-amber-900"
            }`}>
              <Info
                size={13}
                strokeWidth={2.2}
                className={`mt-0.5 shrink-0 ${theme === "high-contrast" ? "text-white" : "text-amber-500 dark:text-amber-400"}`}
              />
              <span className="min-w-0">
                {translations[langMode]?.reportsGenerationNote ?? "Reports can take a few minutes to generate. If yours isn't ready yet, please allow some time and click the refresh button to check again. Reports will expire after 6 hours once generated."}
              </span>
            </div>
          </div>
          <div className={`flex min-h-[22rem] sm:min-h-0 flex-none sm:flex-1 min-w-0 flex-col rounded-xl border overflow-hidden ${theme === "dark" ? "border-gray-700 bg-gray-800/50" : theme === "high-contrast" ? "high-contrast border-2" : "border-slate-200 bg-white shadow-sm"}`}>
            <div className={`relative z-20 flex shrink-0 flex-wrap items-center justify-between gap-2 border-b px-3 py-2 sm:gap-3 sm:px-5 sm:py-2.5 ${theme === "dark" ? "border-gray-700 bg-gray-800/50" : "border-slate-100 bg-white"}`}>
              <p className="flex items-center gap-3 text-xs text-slate-500 dark:text-gray-400 sm:gap-4">
                <span className="whitespace-nowrap">
                  {`${translations[langMode].showing || "Showing"} ${displayStart}-${displayEnd} ${translations[langMode].of || "of"} ${totalItems ?? 0}`}
                </span>
              </p>

              <div className="flex w-full min-w-0 items-center justify-between gap-2 text-xs text-slate-500 dark:text-gray-400 sm:w-auto sm:justify-end sm:gap-3">
                <div className="flex items-center gap-1.5">
                  <label htmlFor="pageSize" className="whitespace-nowrap">
                    {translations[langMode].rows}
                  </label>
                  <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} height="32px" fontSize="12px" />
                </div>
                <span className="pager-nav flex items-center gap-1">
                  <PagerArrowButton
                    direction="prev"
                    isRtl={isRtl}
                    onClick={goToPreviousPage}
                    disabled={currentPage === 0}
                    ariaLabel={translations[langMode]?.previousPage || "Previous page"}
                    className="!h-9 !w-9 sm:!h-8 sm:!w-8"
                  />
                  <span className="px-1 text-xs text-slate-600 dark:text-gray-400 whitespace-nowrap">
                    {currentPage + 1} / {Math.max(totalPages + 1, 1)}
                  </span>
                  <PagerArrowButton
                    direction="next"
                    isRtl={isRtl}
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    ariaLabel={translations[langMode]?.nextPage || "Next page"}
                    className="!h-9 !w-9 sm:!h-8 sm:!w-8"
                  />
                </span>
              </div>
            </div>

            <TableScrollShell
              theme={theme}
              className="flex-1 min-h-0"
              innerClassName="task-list-scrollbar mobile-hide-scrollbar h-full max-h-none rounded-none border-0 shadow-none"
              maxHeight="none"
            >
                <table className="min-w-[640px] w-full text-xs border-collapse" dir={isRtl ? "rtl" : "ltr"}>
                  <thead className={`sticky top-0 z-10 border-b ${theme === "dark"
                    ? "border-gray-700 bg-gray-800 text-gray-200"
                    : theme === "high-contrast"
                      ? "high-contrast bg-gray-100"
                      : "border-slate-200 bg-slate-50 text-slate-500"
                  }`}>
                    <tr className={theme === "high-contrast" ? "high-contrast" : ""}>
                      <th className="px-5 py-3.5 text-center font-semibold text-xs uppercase tracking-[0.04em] w-14">
                        No.
                      </th>
                      <th className="px-5 py-3.5 text-left rtl:text-right font-semibold text-xs uppercase tracking-[0.04em] min-w-[280px]">
                        <div className={`flex items-center gap-3 ${isRtl ? "flex-row-reverse" : ""}`}>
                          <span className="shrink-0 whitespace-nowrap">{translations[langMode].WorkflowNames}</span>
                          <div
                            className={`flex min-w-0 flex-1 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 normal-case ${isRtl ? "flex-row-reverse" : ""} ${theme === "dark"
                              ? "bg-gray-700 border-gray-600"
                              : "border-gray-300 bg-white"
                              }`}
                          >
                            <Search className="h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                            <input
                              type="text"
                              value={workflowNameSearch}
                              onChange={(e) => setWorkflowNameSearch(e.target.value)}
                              placeholder={translations[langMode].search || "Search..."}
                              className={`w-full min-w-0 h-5 text-xs bg-transparent border-0 p-0 focus:ring-0 focus:outline-none placeholder-gray-400 tracking-normal font-normal ${theme === "dark"
                                ? "text-gray-200 placeholder-gray-500"
                                : "text-gray-800"
                                }`}
                              aria-label="Search workflow names"
                            />
                          </div>
                        </div>
                      </th>
                      <th className="px-5 py-3.5 text-left rtl:text-right font-semibold text-xs uppercase tracking-[0.04em] min-w-[190px] whitespace-nowrap">
                        {translations[langMode].ReportLink}
                      </th>
                      <th className="px-5 py-3.5 text-left rtl:text-right font-semibold text-xs uppercase tracking-[0.04em] min-w-[160px]">
                        {translations[langMode].performancereport}
                      </th>
                      <th className="px-5 py-3.5 text-left rtl:text-right font-semibold text-xs uppercase tracking-[0.04em] min-w-[160px]">
                        {translations[langMode].RequestedAt}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="5" className="px-5 py-12 text-center">
                          <Spinner />
                        </td>
                      </tr>
                    ) : totalValue === 0 || filteredReportData.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-5 py-12">
                          <div className={`flex flex-col items-center justify-center gap-1 text-center ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}>
                            <Inbox size={28} strokeWidth={1.5} className="mb-1 opacity-70" />
                            <span className="text-sm">{translations[langMode].noreportdataavb}</span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredReportData.map((report, index) => {
                        const originalIndex = reportData.indexOf(report);
                        const rowNumber =
                          currentPage * pageSize + originalIndex + 1;
                        // reportRequestType decides which column the row belongs to
                        // (and which link field it carries); the other column is "—".
                        // Status comes from reportFailed + that link -- never
                        // reportAvailable, which is already true while generating.
                        const isPerformance = isPerformanceReport(report);
                        const reportStatus = getReportStatus(report);
                        const notApplicable = (
                          <span className={theme === "dark" ? "text-gray-500" : "text-slate-400"}>
                            —
                          </span>
                        );
                        const statusCell =
                          reportStatus === REPORT_STATUS.FAILED ? (
                            <ReportFailedStatus errorMessage={report.errorMessage} />
                          ) : reportStatus === REPORT_STATUS.NO_RECORDS ? (
                            <ReportNoRecordsStatus message={report.errorMessage} />
                          ) : reportStatus === REPORT_STATUS.READY ? (
                            <Tooltip text={translations[langMode].downloadreporttooltip} position="top">
                                <a
                                  href={getReportLink(report)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`inline-flex items-center justify-center gap-1.5 rounded-[8px] px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs transition-all duration-150 hover:shadow whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                                    theme === "high-contrast"
                                      ? "high-contrast border-2"
                                      : "bg-[#236DB4] hover:bg-[#1d5a96] focus:ring-[#236DB4]/40"
                                  }`}
                                >
                                  <Download size={13} className="shrink-0" />
                                  <span>{translations[langMode].download}</span>
                                </a>
                              </Tooltip>
                          ) : (
                            <span
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium whitespace-nowrap shadow-xs ${
                                theme === "dark"
                                  ? "border-amber-500/30 bg-amber-500/15 text-amber-300"
                                  : theme === "high-contrast"
                                    ? "high-contrast border-2 font-bold"
                                    : "border-amber-200/90 bg-amber-50/80 text-amber-800"
                              }`}
                            >
                              <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-amber-500" />
                              <span>{translations[langMode].reportGenerating}</span>
                            </span>
                          );
                        return (
                          <tr
                            key={index}
                            className={`text-xs transition-colors duration-150 ${theme === "dark"
                              ? "border-b border-gray-700 hover:bg-gray-700/50 bg-gray-900/50"
                              : "border-b border-slate-100 bg-white hover:bg-slate-50/70"
                              }`}
                          >
                            <td className="px-5 py-3.5 text-center font-medium text-slate-500 dark:text-gray-400 tabular-nums">
                              {rowNumber}
                            </td>
                            <td className="px-5 py-3.5 max-w-xs text-slate-700 dark:text-gray-200 break-words">
                              {report.workflowNames}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap">
                              {isPerformance ? notApplicable : statusCell}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap">
                              {isPerformance ? statusCell : notApplicable}
                            </td>
                            <td className="px-5 py-3.5 text-slate-500 dark:text-gray-400 whitespace-nowrap">
                              {formattedDate(report.createdAt)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
            </TableScrollShell>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportData;
